import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { NewFindingsFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newFindingTriggerModel';
import { PlaybooksTriggerConfigInput } from 'src/app/models/componentInputs/playbooksTriggerInput.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject } from '@angular/core';
import { DetectionRulesHttpService } from 'src/app/services/httpServices/governance/detectionRulesHttpService/detection-rules-http.service';
import { Severity, ViolationRule } from 'src/app/models/api/governance/violationRule.model';
import { DetectionRuleSeverity } from 'src/app/models/api/finding.model';
import { AiService } from 'src/app/models/api/agentStep.model';
import { createSelectOptionsFromEnum } from 'src/app/common/selectOptionsUtils';
import { cloneDeep } from 'lodash';

@Component({
  selector: 'app-new-finding-trigger',
  templateUrl: './new-finding-trigger.component.html',
  styleUrls: ['./new-finding-trigger.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewFindingTriggerComponent implements OnInit, OnChanges {
  @Input() public fg: UntypedFormGroup;
  @Input() public playbooksTriggerInput: PlaybooksTriggerConfigInput;
  @Input() public policyIds: string[] = [];
  @Input() public detectionRules?: ViolationRule[];
  @Output() public validConfiguration = new EventEmitter<boolean>();

  public ruleOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public severityOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public aiServiceOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  private _allAvailableRules: ViolationRule[] = [];

  private readonly _destroyRef = inject(DestroyRef);

  constructor(
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _beautifyPipe: BeautifyDisplayStringPipe,
    private readonly _detectionRulesService: DetectionRulesHttpService
  ) {}

  public ngOnInit() {
    this._initializeForm();
    this._loadRuleOptions();
    this._setupFormValidation();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes['detectionRules']) {
      this._loadRuleOptions();
    }
  }

  private _initializeForm() {
    if (!this.fg.contains('ruleIds')) {
      this.fg.addControl('ruleIds', this._formBuilder.control([]));
    }
    if (!this.fg.contains('ruleSeverity')) {
      this.fg.addControl('ruleSeverity', this._formBuilder.control([]));
    }
    if (!this.fg.contains('aiServices')) {
      this.fg.addControl('aiServices', this._formBuilder.control([]));
    }

    if (this.playbooksTriggerInput.triggerConfig) {
      const config = this.playbooksTriggerInput.triggerConfig as NewFindingsFoundTriggerConfigModel;
      this.fg.patchValue({
        ruleIds: config.ruleIds || [],
        ruleSeverity: config.ruleSeverity || [],
        aiServices: config.aiServices || [],
      });
    }
  }

  private _loadRuleOptions() {
    if (this.detectionRules && this.detectionRules.length > 0) {
      this._loadRuleOptionsFromProvidedRules();
    } else {
      this._loadRuleOptionsFromApi();
    }
  }

  private _loadRuleOptionsFromProvidedRules() {
    if (!this.detectionRules) {
      return;
    }

    const availableRules = this.detectionRules.filter((rule) => rule.status === 'on');
    this._processAvailableRules(availableRules);
  }

  private _loadRuleOptionsFromApi() {
    const integrationId = this.playbooksTriggerInput.integration.id;

    if (!integrationId) {
      return;
    }

    this._detectionRulesService.getDetectionRules(integrationId).subscribe({
      next: (rules: ViolationRule[]) => {
        const availableRules = rules.filter((rule) => rule.status === 'on');
        this._processAvailableRules(availableRules);
      },
      error: (error) => {
        console.error('Failed to load detection rules:', error);
        this._handleRuleLoadingError();
      },
    });
  }

  private _processAvailableRules(availableRules: ViolationRule[]) {
    this._allAvailableRules = cloneDeep(availableRules);
    this._updateRuleOptions(availableRules);

    if (availableRules?.length > 0) {
      this._loadAiServiceOptionsFromRules(availableRules);
      this._loadSeverityOptionsFromRules(availableRules);
    } else {
      this._loadAiServiceOptionsFromEnum();
      this._loadSeverityOptionsFromEnum();
    }
  }

  private _handleRuleLoadingError() {
    this.ruleOptions$.next([]);
    this._loadAiServiceOptionsFromEnum();
    this._loadSeverityOptionsFromEnum();
  }

  private _loadSeverityOptionsFromEnum() {
    const options = createSelectOptionsFromEnum(DetectionRuleSeverity, this._beautifyPipe);
    this.severityOptions$.next(options);
  }

  private _loadSeverityOptionsFromRules(rules: ViolationRule[]) {
    const uniqueSeverities = new Set(rules.map((rule) => rule.severity).filter((severity) => severity));

    const options = Array.from(uniqueSeverities).map((severity) => ({
      id: severity,
      value: severity,
      displayName: this._beautifyPipe.transform(severity),
    }));

    this.severityOptions$.next(options);
  }

  private _loadAiServiceOptionsFromEnum() {
    const options = createSelectOptionsFromEnum(AiService, this._beautifyPipe);
    this.aiServiceOptions$.next(options);
  }

  private _loadAiServiceOptionsFromRules(rules: ViolationRule[]) {
    const uniqueAiServices = new Set(rules.map((rule) => rule.aiService).filter((service) => service));

    const options = Array.from(uniqueAiServices).map((service) => ({
      id: service as string,
      value: service as string,
      displayName: this._beautifyPipe.transform(service),
    }));

    this.aiServiceOptions$.next(options);
  }

  private _setupFormValidation() {
    this.fg.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      const isValid = this._isFormValid();
      this.validConfiguration.emit(isValid);
    });

    this.fg
      .get('ruleIds')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((selectedRuleIds) => {
        this._updateSeverityOptionsBasedOnSelectedRules(selectedRuleIds);
      });

    this.fg
      .get('aiServices')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((selectedAiServices) => {
        this._updateRuleOptionsBasedOnSelectedAiServices(selectedAiServices);
      });
  }

  private _updateSeverityOptionsBasedOnSelectedRules(selectedRuleIds: string[]) {
    let rulesToConsider: ViolationRule[];

    if (!selectedRuleIds || selectedRuleIds.length === 0) {
      rulesToConsider = this._allAvailableRules;
    } else {
      const selectedRuleIdsSet = new Set(selectedRuleIds);
      rulesToConsider = this._allAvailableRules.filter((rule) => selectedRuleIdsSet.has(rule.id));
    }

    this._loadSeverityOptionsFromRules(rulesToConsider);
    this._clearInvalidSeveritySelections(rulesToConsider);
  }

  private _updateRuleOptionsBasedOnSelectedAiServices(selectedAiServices: string[]) {
    let filteredRules: ViolationRule[];

    if (!selectedAiServices || selectedAiServices.length === 0) {
      filteredRules = this._allAvailableRules;
    } else {
      const selectedAiServicesSet = new Set(selectedAiServices);
      filteredRules = this._allAvailableRules.filter((rule) => selectedAiServicesSet.has(rule.aiService as string));
    }

    this._updateRuleOptions(filteredRules);

    this._clearInvalidRuleSelections(filteredRules);
  }

  private _updateRuleOptions(rules: ViolationRule[]) {
    const options = rules.map((rule) => ({
      id: rule.id,
      value: rule.id,
      displayName: `${rule.id} - ${this._beautifyPipe.transform(rule.aiService)} - ${rule.displayName}`,
    }));
    this.ruleOptions$.next(options);
  }

  private _clearInvalidRuleSelections(availableRules: ViolationRule[]) {
    const currentSelectedRuleIds = this.fg.get('ruleIds')?.value || [];
    const availableRuleIdsSet = new Set(availableRules.map((rule) => rule.id));

    const validSelections = currentSelectedRuleIds.filter((ruleId: string) => availableRuleIdsSet.has(ruleId));

    if (validSelections.length !== currentSelectedRuleIds.length) {
      this.fg.get('ruleIds')?.setValue(validSelections, { emitEvent: false });
    }
  }

  private _clearInvalidSeveritySelections(availableRules: ViolationRule[]) {
    const currentSelectedSeverities = this.fg.get('ruleSeverity')?.value || [];
    const availableSeveritiesSet = new Set(availableRules.map((rule) => rule.severity).filter((severity) => severity));

    const validSelections = currentSelectedSeverities.filter((severity: Severity) =>
      availableSeveritiesSet.has(severity)
    );

    if (validSelections.length !== currentSelectedSeverities.length) {
      this.fg.get('ruleSeverity')?.setValue(validSelections, { emitEvent: false });
    }
  }

  private _isFormValid(): boolean {
    const ruleIds = this.fg.get('ruleIds')?.value;
    const ruleSeverity = this.fg.get('ruleSeverity')?.value;
    const aiServices = this.fg.get('aiServices')?.value;

    return (
      (ruleIds && ruleIds.length > 0) ||
      (ruleSeverity && ruleSeverity.length > 0) ||
      (aiServices && aiServices.length > 0)
    );
  }

  public updateMultiSelectControl(controlName: string, options: ISelectOption[]) {
    const selectedValues = options
      .filter((option) => option && option.value !== undefined)
      .map((option) => option.value);
    const control = this.fg.get(controlName);
    if (control) {
      control.setValue(selectedValues);
    }
  }
}
